<template>
  <figure ref="root" class="hub-topology" :class="{ 'is-live': live }">
    <svg
      class="ht-svg"
      viewBox="0 0 600 600"
      role="img"
      :aria-label="$t('Home.section2.diagram.aria')"
      focusable="false"
    >
      <!-- Point-to-point mesh: what the hub replaces. Dissolves on entrance. -->
      <g class="ht-mesh" aria-hidden="true">
        <line
          v-for="(pair, index) in meshPairs"
          :key="'mesh-' + index"
          class="ht-mesh-line"
          :style="{ transitionDelay: index * 18 + 'ms' }"
          :x1="nodes[pair[0]].x"
          :y1="nodes[pair[0]].y"
          :x2="nodes[pair[1]].x"
          :y2="nodes[pair[1]].y"
        />
      </g>

      <g class="ht-orbit" aria-hidden="true">
        <circle :cx="center" :cy="center" :r="radius" />
      </g>

      <!-- One connection per party -->
      <g class="ht-spokes" aria-hidden="true">
        <line
          v-for="(node, index) in nodes"
          :key="'spoke-' + index"
          class="ht-spoke"
          :class="{
            'is-request': route && routeLeg(index) === 'request',
            'is-response': route && routeLeg(index) === 'response'
          }"
          pathLength="1"
          :style="{ transitionDelay: 500 + index * 70 + 'ms' }"
          :x1="node.sx"
          :y1="node.sy"
          :x2="node.ex"
          :y2="node.ey"
        />
      </g>

      <g class="ht-hub" aria-hidden="true">
        <circle class="ht-hub-halo" :cx="center" :cy="center" :r="hubRadius + 18" />
        <circle class="ht-hub-core" :cx="center" :cy="center" :r="hubRadius" />
        <circle
          v-if="hubPulse"
          :key="'hub-pulse-' + hubPulse"
          class="ht-pulse ht-pulse--hub"
          :cx="center"
          :cy="center"
          :r="hubRadius"
        />
        <image
          class="ht-logo ht-logo--dark"
          :href="logoDark"
          :x="center - 46"
          :y="center - 37"
          width="92"
          height="74"
        />
        <image
          class="ht-logo ht-logo--light"
          :href="logoLight"
          :x="center - 46"
          :y="center - 37"
          width="92"
          height="74"
        />
      </g>

      <g class="ht-nodes" aria-hidden="true">
        <g
          v-for="(node, index) in nodes"
          :key="'node-' + index"
          class="ht-node"
          :class="['ht-node--' + node.kind, { 'is-active': route && routeLeg(index) }]"
        >
          <circle
            v-if="nodePulse && nodePulse.index === index"
            :key="'node-pulse-' + nodePulse.id"
            class="ht-pulse"
            :class="'ht-pulse--' + nodePulse.tone"
            :cx="node.x"
            :cy="node.y"
            :r="nodeRadius"
          />
          <circle class="ht-node-core" :cx="node.x" :cy="node.y" :r="nodeRadius" />
          <text class="ht-node-label" :x="node.x" :y="node.y" dy="0.35em" text-anchor="middle">
            {{ $t('Home.section2.diagram.' + node.kind) }}
          </text>
        </g>
      </g>

      <circle
        v-if="packet.visible"
        class="ht-packet"
        :class="'ht-packet--' + packet.tone"
        :cx="packet.x"
        :cy="packet.y"
        r="6"
        aria-hidden="true"
      />
    </svg>
  </figure>
</template>

<script>
const NODE_KINDS = ['cpo', 'emsp', 'cpo', 'emsp', 'cpo', 'emsp', 'cpo', 'emsp']

// One roaming exchange: eMSP request travels in via the hub to a CPO (purple),
// the CPO's response travels back the same way (pink answers purple).
const LEGS = [
  { from: 'origin', to: 'hub', start: 0, end: 650, tone: 'request' },
  { from: 'hub', to: 'target', start: 850, end: 1500, tone: 'request' },
  { from: 'target', to: 'hub', start: 1900, end: 2550, tone: 'response' },
  { from: 'hub', to: 'origin', start: 2750, end: 3400, tone: 'response' }
]
const CYCLE = 4200
const ENTRANCE = 1600

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
}

export default {
  name: 'HubTopology',
  data() {
    return {
      center: 300,
      radius: 228,
      hubRadius: 70,
      nodeRadius: 34,
      live: false,
      route: null,
      packet: { visible: false, x: 300, y: 300, tone: 'request' },
      hubPulse: 0,
      nodePulse: null,
      logoDark: require('@/assets/images/Newlandindpage/newimage/Dark Mode2_1@4x.png'),
      logoLight: require('@/assets/images/Newlandindpage/newimage/Light Mode2_1@4x.png')
    }
  },
  computed: {
    nodes() {
      return NODE_KINDS.map((kind, index) => {
        const angle = ((-67.5 + index * 45) * Math.PI) / 180
        const x = this.center + this.radius * Math.cos(angle)
        const y = this.center + this.radius * Math.sin(angle)
        const dx = Math.cos(angle)
        const dy = Math.sin(angle)
        return {
          kind,
          x,
          y,
          // Spoke runs from the node's edge to the hub's edge
          sx: x - dx * this.nodeRadius,
          sy: y - dy * this.nodeRadius,
          ex: this.center + dx * (this.hubRadius + 6),
          ey: this.center + dy * (this.hubRadius + 6)
        }
      })
    },
    meshPairs() {
      const pairs = []
      for (let i = 0; i < this.nodes.length; i++) {
        for (let j = i + 1; j < this.nodes.length; j++) pairs.push([i, j])
      }
      return pairs
    }
  },
  mounted() {
    this.reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.visible = false
    this.cycle = -1
    this.lastTarget = -1

    if (this.reducedMotion || !('IntersectionObserver' in window)) {
      this.live = true
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting
        if (this.visible && !this.live) {
          this.live = true
          this.entranceTimer = setTimeout(() => this.play(), ENTRANCE)
        } else if (this.visible && this.entranceDone) {
          this.play()
        } else {
          this.pause()
        }
      },
      { threshold: 0.25 }
    )
    this.observer.observe(this.$refs.root)
    this.onVisibility = () => (document.hidden ? this.pause() : this.visible && this.play())
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  beforeDestroy() {
    this.pause()
    clearTimeout(this.entranceTimer)
    if (this.observer) this.observer.disconnect()
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility)
  },
  methods: {
    play() {
      this.entranceDone = true
      if (this.frame || this.reducedMotion) return
      this.startedAt = null
      this.cycle = -1
      this.frame = requestAnimationFrame(this.tick)
    },
    pause() {
      if (this.frame) cancelAnimationFrame(this.frame)
      this.frame = null
      this.packet.visible = false
      this.route = null
    },
    pickRoute() {
      const emsp = []
      const cpo = []
      NODE_KINDS.forEach((kind, index) => (kind === 'emsp' ? emsp : cpo).push(index))
      let target = cpo[Math.floor(Math.random() * cpo.length)]
      if (target === this.lastTarget) target = cpo[(cpo.indexOf(target) + 1) % cpo.length]
      this.lastTarget = target
      this.route = { origin: emsp[Math.floor(Math.random() * emsp.length)], target }
    },
    routeLeg(index) {
      if (!this.route || !this.activeLeg) return null
      const leg = this.activeLeg
      const nodeKey = leg.from === 'hub' ? leg.to : leg.from
      return this.route[nodeKey] === index ? leg.tone : null
    },
    point(key) {
      if (key === 'hub') return null
      return this.nodes[this.route[key]]
    },
    tick(now) {
      if (this.startedAt === null) this.startedAt = now
      const elapsed = now - this.startedAt
      const cycle = Math.floor(elapsed / CYCLE)
      const t = elapsed % CYCLE

      if (cycle !== this.cycle) {
        this.cycle = cycle
        this.pickRoute()
      }

      const leg = LEGS.find((item) => t >= item.start && t < item.end) || null
      if (leg !== this.activeLeg) this.onLegChange(this.activeLeg, leg)
      this.activeLeg = leg

      if (leg) {
        const progress = easeInOut((t - leg.start) / (leg.end - leg.start))
        const from = this.point(leg.from)
        const to = this.point(leg.to)
        const ax = from ? from.sx : to.ex
        const ay = from ? from.sy : to.ey
        const bx = to ? to.sx : from.ex
        const by = to ? to.sy : from.ey
        this.packet.x = ax + (bx - ax) * progress
        this.packet.y = ay + (by - ay) * progress
        this.packet.tone = leg.tone
        this.packet.visible = true
      } else {
        this.packet.visible = false
      }

      this.frame = requestAnimationFrame(this.tick)
    },
    onLegChange(previous) {
      if (!previous) return
      // The packet just arrived somewhere: light up the receiver
      if (previous.to === 'hub') {
        this.hubPulse += 1
      } else {
        this.nodePulse = {
          index: this.route[previous.to],
          tone: previous.tone,
          id: Date.now()
        }
      }
    }
  }
}
</script>
